import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { User, Building2, KeyRound, ImageIcon } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getMyProfile } from "../../services/accountService";
import Loader from "../../components/common/Loader";
import Tabs from "../../components/common/Tabs";
import ProfileTab from "../../components/settings/ProfileTab";
import BusinessDetailsTab from "../../components/settings/BusinessDetailsTab";
import PasswordTab from "../../components/settings/PasswordTab";
import PromoImageTab from "../../components/settings/PromoImageTab";
import { color } from "../../styles/theme";

/**
 * Account & Business Settings — consolidates mobile's profile-update,
 * add-business-detail, and change-password screens into one tabbed page,
 * matching the pattern already used for Event Details
 * (src/pages/events/EventDetails.jsx). See the approved Phase 1 plan for the
 * full scope/rationale.
 */
const AccountSettings = () => {
  const { t } = useTranslation(["settings", "common"]);
  const { user, updateUser, isSubAccount } = useAuth();
  const [loading, setLoading] = useState(true);
  const isPromoter = user?.UserType === "Promoter";
  const [activeTab, setActiveTab] = useState("Profile");

  useEffect(() => {
    const controller = new AbortController();
    getMyProfile({ signal: controller.signal })
      .then((res) => {
        const fresh = res.data?.Result || res.data;
        if (fresh) updateUser(fresh);
      })
      .catch(() => {
        if (!controller.signal.aborted) toast.error(t("accountSettings.toasts.loadFailed"));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    { key: "Profile", label: t("accountSettings.tabs.profile"), icon: User },
    ...(isPromoter ? [{ key: "Business Details", label: t("accountSettings.tabs.businessDetails"), icon: Building2 }] : []),
    { key: "Password", label: t("accountSettings.tabs.password"), icon: KeyRound },
    ...(!isSubAccount ? [{ key: "Promo Image", label: t("accountSettings.tabs.promoImage"), icon: ImageIcon }] : []),
  ];

  if (loading) return <Loader />;

  return (
    <div>
      <h2 style={s.title}>{t("accountSettings.title")}</h2>

      <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

      <div style={s.tabContent}>
        {activeTab === "Profile" && <ProfileTab />}
        {activeTab === "Business Details" && isPromoter && <BusinessDetailsTab />}
        {activeTab === "Password" && <PasswordTab />}
        {activeTab === "Promo Image" && <PromoImageTab />}
      </div>
    </div>
  );
};

const s = {
  title: { fontSize: 20, fontWeight: 800, color: color.textPrimary, marginBottom: 18, letterSpacing: -0.3 },
  tabContent: { maxWidth: 720 },
};

export default AccountSettings;
